import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { SessionSubtype } from '@/lib/schemas/db'
import { FormCapsLabel } from '@/components/ui/form-caps-label'
import { FormFieldLabel } from '@/components/ui/form-field-label'
import { UnderlineDateInput } from '@/components/ui/underline-date-input'
import { UnderlineInput } from '@/components/ui/underline-input'
import { UnderlineTextarea } from '@/components/ui/underline-textarea'
import { AutocompleteCustomerContainer } from '@/components/AutocompleteCustomerContainer'
import { type SearchOption } from '@/components/ui/Search'
import { TimeSelect } from '@/components/ui/time-select'
import {
  addHourAfter,
  addHourBefore,
  isTimeOnOrAfter,
} from '@/lib/timeWindow'

export type NewCalloutMode = 'create' | 'edit'

export interface NewCalloutInitialValues {
  day: string
  /** Start time for the Callout (HH:mm), if not whole-day. */
  time?: string
  /** End time for the Callout (HH:mm), if not whole-day. */
  endTime?: string
  /** Whether this is a whole-day booking (date only, time is vague). */
  wholeDay?: boolean
  /**
   * ID of the linked Customer, if this Callout is associated to an existing one.
   * When omitted, containers may create a new Customer on save.
   */
  customerId?: string
  customerName: string
  address: string
  phone: string
  email: string
  invoiceTo: string
  vat?: string
  invoiceAddress?: string
  invoiceEmail?: string
  issueSummary: string
  notes?: string
  sessionSubtype?: SessionSubtype
}

export interface NewCalloutScreenProps {
  mode?: NewCalloutMode
  initialValues?: NewCalloutInitialValues
  showHeader?: boolean
  errorMessage?: string | null
  bookingExtra?: ReactNode
  /**
   * Renders below the day/time inputs with the live picked values — used to
   * show that day's existing bookings so conflicts surface while picking.
   * A render prop so this screen stays free of data-layer dependencies.
   */
  renderDayBookings?: (booking: {
    day: string
    time: string
    endTime: string
  }) => ReactNode
  onSave?: (values: NewCalloutInitialValues) => void | Promise<void>
  onCancel?: () => void
}

type CustomerAutocompleteOption = SearchOption & {
  fullName?: string
  contactAddress?: string
  contactPhone?: string
  contactEmail?: string
  invoiceName?: string
  invoiceAddress?: string
  invoiceEmail?: string
  invoiceVat?: string
}

const defaultInitialValues: NewCalloutInitialValues = {
  day: '',
  customerName: '',
  address: '',
  phone: '',
  email: '',
  invoiceTo: '',
  issueSummary: '',
}

const defaultStartTime = '06:00'
const defaultEndTime = '07:00'

function getInitialBookingTimes(initial: NewCalloutInitialValues) {
  return {
    time: initial.time ?? defaultStartTime,
    endTime: initial.endTime ?? defaultEndTime,
  }
}

type NewCalloutFieldNames = {
  address: string
  phone: string
  email: string
  invoiceTo: string
  vat: string
  invoiceEmail: string
  invoiceAddress: string
  issueSummary: string
  notes: string
}

function createRandomSuffix(length = 5): string {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789'
  let out = ''
  for (let i = 0; i < length; i += 1) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)]
  }
  return out
}

function createNewCalloutFieldNames(): NewCalloutFieldNames {
  const suffix = createRandomSuffix(5)
  return {
    address: `service_location_${suffix}`,
    phone: `contact_mobile_${suffix}`,
    email: `contact_mailbox_${suffix}`,
    invoiceTo: `billing_recipient_${suffix}`,
    vat: `billing_vat_ref_${suffix}`,
    invoiceEmail: `billing_mailbox_${suffix}`,
    invoiceAddress: `billing_location_${suffix}`,
    issueSummary: `callout_summary_${suffix}`,
    notes: `tech_notes_${suffix}`,
  }
}

function NewCalloutFormSections(
  props: {
    initial: NewCalloutInitialValues
    day: string
    onDayChange: (value: string) => void
    time: string
    endTime: string
    onTimeChange: (value: string) => void
    onEndTimeChange: (value: string) => void
    selectedCustomer: SearchOption | null
    onSelectedCustomerChange: (value: SearchOption | null) => void
    address: string
    phone: string
    email: string
    invoiceTo: string
    vat: string
    invoiceAddress: string
    invoiceEmail: string
    onAddressChange: (value: string) => void
    onPhoneChange: (value: string) => void
    onEmailChange: (value: string) => void
    onInvoiceToChange: (value: string) => void
    onVatChange: (value: string) => void
    onInvoiceAddressChange: (value: string) => void
    onInvoiceEmailChange: (value: string) => void
    fieldNames: NewCalloutFieldNames
    isEdit?: boolean
    bookingExtra?: ReactNode
    renderDayBookings?: NewCalloutScreenProps['renderDayBookings']
  },
) {
  const {
    initial,
    day,
    onDayChange,
    time,
    endTime,
    onTimeChange,
    onEndTimeChange,
    selectedCustomer,
    onSelectedCustomerChange,
    address,
    phone,
    email,
    invoiceTo,
    vat,
    invoiceAddress,
    invoiceEmail,
    onAddressChange,
    onPhoneChange,
    onEmailChange,
    onInvoiceToChange,
    onVatChange,
    onInvoiceAddressChange,
    onInvoiceEmailChange,
    fieldNames,
    isEdit,
    bookingExtra,
    renderDayBookings,
  } = props

  const stackedLabelClass = 'mb-1 block text-[11px] text-slate-500'

  return (
    <div className="space-y-5 text-xs">
      <section className="space-y-2.5">
        <FormCapsLabel>Booking</FormCapsLabel>
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <div>
            <FormFieldLabel className={stackedLabelClass}>Day</FormFieldLabel>
            <UnderlineDateInput
              className="w-[10.5rem]"
              name="day"
              value={day}
              onChange={(event) => onDayChange(event.target.value)}
            />
          </div>
          <div>
            <FormFieldLabel className={stackedLabelClass}>From</FormFieldLabel>
            <TimeSelect
              className="w-[7rem]"
              name="time"
              value={time}
              onChange={(event) => onTimeChange(event.target.value)}
            />
          </div>
          <div>
            <FormFieldLabel className={stackedLabelClass}>To</FormFieldLabel>
            <TimeSelect
              className="w-[7rem]"
              name="endTime"
              value={endTime}
              onChange={(event) => onEndTimeChange(event.target.value)}
            />
          </div>
        </div>
        <label
          htmlFor="wholeDay"
          className="flex cursor-pointer items-center gap-2 text-[11px] text-slate-500"
        >
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-slate-900"
            id="wholeDay"
            name="wholeDay"
            defaultChecked={initial.wholeDay ?? false}
          />
          Whole day — treat the exact time as flexible.
        </label>
        {bookingExtra}
        {renderDayBookings ? renderDayBookings({ day, time, endTime }) : null}
      </section>

      <section className="space-y-2.5">
        <FormCapsLabel>Customer</FormCapsLabel>
        <div>
          <FormFieldLabel className={stackedLabelClass}>Customer</FormFieldLabel>
          <AutocompleteCustomerContainer
            value={selectedCustomer}
            onChange={onSelectedCustomerChange}
            ariaLabel="Customer"
            lockAfterSelect
          />
        </div>
        <div>
          <FormFieldLabel className={stackedLabelClass}>Address</FormFieldLabel>
          <UnderlineTextarea
            className="w-full"
            name={fieldNames.address}
            autoComplete="new-password"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            rows={2}
            value={address}
            onChange={(event) => onAddressChange(event.target.value)}
          />
        </div>
        <div className="grid gap-x-4 gap-y-2.5 sm:grid-cols-2">
          <div>
            <FormFieldLabel className={stackedLabelClass}>Phone</FormFieldLabel>
            <UnderlineInput
              className="w-full"
              type="tel"
              name={fieldNames.phone}
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={phone}
              onChange={(event) => onPhoneChange(event.target.value)}
            />
          </div>
          <div>
            <FormFieldLabel className={stackedLabelClass}>Email</FormFieldLabel>
            <UnderlineInput
              className="w-full"
              type="email"
              name={fieldNames.email}
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={email}
              onChange={(event) => onEmailChange(event.target.value)}
            />
          </div>
        </div>
      </section>

      <section className="space-y-2.5">
        <FormCapsLabel>Invoice</FormCapsLabel>
        <div className="grid gap-x-4 gap-y-2.5 sm:grid-cols-2">
          <div>
            <FormFieldLabel className={stackedLabelClass}>
              Invoice to
            </FormFieldLabel>
            <UnderlineInput
              className="w-full"
              placeholder="Who to invoice"
              name={fieldNames.invoiceTo}
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={invoiceTo}
              onChange={(event) => onInvoiceToChange(event.target.value)}
            />
          </div>
          <div>
            <FormFieldLabel className={stackedLabelClass}>
              VAT (optional)
            </FormFieldLabel>
            <UnderlineInput
              className="w-full"
              placeholder="VAT number"
              name={fieldNames.vat}
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={vat}
              onChange={(event) => onVatChange(event.target.value)}
            />
          </div>
          <div>
            <FormFieldLabel className={stackedLabelClass}>
              Invoice email
            </FormFieldLabel>
            <UnderlineInput
              className="w-full"
              type="email"
              placeholder="Invoice contact email"
              name={fieldNames.invoiceEmail}
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={invoiceEmail}
              onChange={(event) => onInvoiceEmailChange(event.target.value)}
            />
          </div>
          <div>
            <FormFieldLabel className={stackedLabelClass}>
              Invoice address
            </FormFieldLabel>
            <UnderlineTextarea
              className="w-full"
              rows={2}
              placeholder="Use customer or callout address by default"
              name={fieldNames.invoiceAddress}
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={invoiceAddress}
              onChange={(event) => onInvoiceAddressChange(event.target.value)}
            />
          </div>
        </div>
      </section>

      <section className="space-y-2.5 rounded-md border border-amber-100 bg-amber-50/60 p-3">
        <FormCapsLabel>Summary</FormCapsLabel>
        <div>
          <FormFieldLabel className={stackedLabelClass}>
            Issue summary
          </FormFieldLabel>
          <UnderlineInput
            className="w-full"
            name={fieldNames.issueSummary}
            autoComplete="new-password"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            defaultValue={initial.issueSummary}
          />
        </div>
        {!isEdit ? (
          <div>
            <FormFieldLabel className={stackedLabelClass}>Notes</FormFieldLabel>
            <UnderlineTextarea
              className="w-full"
              rows={3}
              placeholder="Any extra information for the technician…"
              name={fieldNames.notes}
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              defaultValue={initial.notes ?? ''}
            />
          </div>
        ) : null}
      </section>
    </div>
  )
}

export function NewCalloutScreen(props?: NewCalloutScreenProps) {
  const mode = props?.mode ?? 'create'
  const isEdit = mode === 'edit'
  const initial = props?.initialValues ?? defaultInitialValues
  const showHeader = props?.showHeader ?? true
  const errorMessage = props?.errorMessage ?? null
  const bookingExtra = props?.bookingExtra

  const errorNotice = errorMessage ? (
    <p role="alert" className="text-xs text-red-600 whitespace-pre-line">
      {errorMessage}
    </p>
  ) : null

  const [selectedCustomer, setSelectedCustomer] =
    useState<SearchOption | null>(() => {
      if (!initial.customerName) return null
      return { id: null, name: initial.customerName }
    })

  const [address, setAddress] = useState(() => initial.address ?? '')
  const [phone, setPhone] = useState(() => initial.phone ?? '')
  const [email, setEmail] = useState(() => initial.email ?? '')
  const [invoiceTo, setInvoiceTo] = useState(
    () => initial.invoiceTo ?? '',
  )
  const [vat, setVat] = useState(() => initial.vat ?? '')
  const [invoiceAddress, setInvoiceAddress] = useState(
    () => initial.invoiceAddress ?? '',
  )
  const [invoiceEmail, setInvoiceEmail] = useState(
    () => initial.invoiceEmail ?? initial.email ?? '',
  )
  const [invoiceToOverridden, setInvoiceToOverridden] = useState(false)
  const [invoiceAddressOverridden, setInvoiceAddressOverridden] = useState(false)
  const [invoiceEmailOverridden, setInvoiceEmailOverridden] = useState(false)

  const [day, setDay] = useState(() => initial.day ?? '')
  const initialBookingTimes = getInitialBookingTimes(initial)
  const [time, setTime] = useState(() => initialBookingTimes.time)
  const [endTime, setEndTime] = useState(
    () => initialBookingTimes.endTime,
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isSubmittingRef = useRef(false)

  const fieldNames = useMemo(createNewCalloutFieldNames, [])

  useEffect(() => {
    setSelectedCustomer(
      initial.customerName
        ? { id: initial.customerId ?? null, name: initial.customerName }
        : null,
    )
    setDay(initial.day ?? '')
    setAddress(initial.address ?? '')
    setPhone(initial.phone ?? '')
    setEmail(initial.email ?? '')
    setInvoiceTo(initial.invoiceTo ?? '')
    setVat(initial.vat ?? '')
    setInvoiceAddress(initial.invoiceAddress ?? '')
    setInvoiceEmail(initial.invoiceEmail ?? initial.email ?? '')
    setInvoiceToOverridden(false)
    setInvoiceAddressOverridden(false)
    setInvoiceEmailOverridden(false)

    const nextBookingTimes = getInitialBookingTimes(initial)
    setTime(nextBookingTimes.time)
    setEndTime(nextBookingTimes.endTime)
  }, [
    initial.address,
    initial.customerId,
    initial.customerName,
    initial.day,
    initial.email,
    initial.endTime,
    initial.invoiceAddress,
    initial.invoiceEmail,
    initial.invoiceTo,
    initial.phone,
    initial.time,
    initial.vat,
  ])

  const handleTimeChange = (value: string) => {
    setTime(value)
    setEndTime((currentEnd) =>
      !currentEnd ||
      currentEnd.trim().length === 0 ||
      isTimeOnOrAfter(value, currentEnd)
        ? addHourAfter(value)
        : currentEnd,
    )
  }

  const handleEndTimeChange = (value: string) => {
    setEndTime(value)
    setTime((currentStart) =>
      !currentStart || isTimeOnOrAfter(currentStart, value)
        ? addHourBefore(value)
        : currentStart,
    )
  }

  const handleSelectedCustomerChange = (
    option: SearchOption | null,
  ) => {
    setSelectedCustomer(option)

    if (!option) {
      setInvoiceToOverridden(false)
      setInvoiceAddressOverridden(false)
      setInvoiceEmailOverridden(false)
      return
    }

    if (option.isNew) {
      setInvoiceToOverridden(false)
      setInvoiceAddressOverridden(false)
      setInvoiceEmailOverridden(false)
      return
    }

    const details = option as CustomerAutocompleteOption
    const resolvedName =
      details.invoiceName ??
      details.fullName ??
      option.name ??
      ''

    setAddress(details.contactAddress ?? '')
    setPhone(details.contactPhone ?? '')
    setEmail(details.contactEmail ?? '')
    setInvoiceTo(resolvedName)
    setVat(details.invoiceVat ?? '')
    setInvoiceAddress(details.invoiceAddress ?? '')
    setInvoiceEmail(details.invoiceEmail ?? details.contactEmail ?? '')
    setInvoiceToOverridden(false)
    setInvoiceAddressOverridden(false)
    setInvoiceEmailOverridden(false)
  }

  useEffect(() => {
    if (!selectedCustomer?.isNew) return

    if (!invoiceToOverridden) {
      setInvoiceTo(selectedCustomer.name ?? '')
    }

    if (!invoiceAddressOverridden) {
      setInvoiceAddress(address)
    }

    if (!invoiceEmailOverridden) {
      setInvoiceEmail(email)
    }
  }, [
    address,
    email,
    invoiceAddressOverridden,
    invoiceEmailOverridden,
    invoiceToOverridden,
    selectedCustomer,
  ])

  const handleAddressChange = (value: string) => {
    setAddress(value)
    if (selectedCustomer?.isNew && !invoiceAddressOverridden) {
      setInvoiceAddress(value)
    }
  }

  const handleEmailChange = (value: string) => {
    setEmail(value)
    if (selectedCustomer?.isNew && !invoiceEmailOverridden) {
      setInvoiceEmail(value)
    }
  }

  const handleInvoiceToChange = (value: string) => {
    setInvoiceTo(value)
    setInvoiceToOverridden(true)
  }

  const handleInvoiceAddressChange = (value: string) => {
    setInvoiceAddress(value)
    setInvoiceAddressOverridden(true)
  }

  const handleInvoiceEmailChange = (value: string) => {
    setInvoiceEmail(value)
    setInvoiceEmailOverridden(true)
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (
    event,
  ) => {
    event.preventDefault()
    if (!props?.onSave || isSubmittingRef.current) return

    const formData = new FormData(event.currentTarget)

    const values: NewCalloutInitialValues = {
      day,
      time: time || undefined,
      endTime: endTime || undefined,
      wholeDay: formData.get('wholeDay') === 'on',
      customerId:
        selectedCustomer && !selectedCustomer.isNew && selectedCustomer.id != null
          ? String(selectedCustomer.id)
          : undefined,
      customerName: selectedCustomer?.name ?? '',
      address: String(formData.get(fieldNames.address) ?? ''),
      phone: String(formData.get(fieldNames.phone) ?? ''),
      email: String(formData.get(fieldNames.email) ?? ''),
      invoiceTo: String(formData.get(fieldNames.invoiceTo) ?? ''),
      vat: (formData.get(fieldNames.vat) as string) || undefined,
      invoiceAddress:
        (formData.get(fieldNames.invoiceAddress) as string) || undefined,
      invoiceEmail:
        (formData.get(fieldNames.invoiceEmail) as string) || undefined,
      issueSummary: String(formData.get(fieldNames.issueSummary) ?? ''),
      notes: (formData.get(fieldNames.notes) as string) || undefined,
      sessionSubtype:
        (formData.get('sessionSubtype') as SessionSubtype) ||
        initial.sessionSubtype,
    }

    isSubmittingRef.current = true
    setIsSubmitting(true)
    try {
      await Promise.resolve(props.onSave(values))
    } finally {
      isSubmittingRef.current = false
      setIsSubmitting(false)
    }
  }

  const actions = (
    <div className="flex justify-end gap-2">
      <button
        type="button"
        className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
        onClick={props?.onCancel}
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={isSubmitting}
        className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
      >
        {isSubmitting ? (
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
        ) : null}
        {isSubmitting
          ? isEdit
            ? 'Saving changes…'
            : 'Booking…'
          : isEdit
            ? 'Save changes'
            : 'Book'}
      </button>
    </div>
  )

  if (!showHeader) {
    return (
      <div className="text-ink">
        <form
          className="flex flex-col gap-6"
          autoComplete="new-password"
          onSubmit={handleSubmit}
        >
          <NewCalloutFormSections
            initial={initial}
            day={day}
            onDayChange={setDay}
            time={time}
            endTime={endTime}
            onTimeChange={handleTimeChange}
            onEndTimeChange={handleEndTimeChange}
            selectedCustomer={selectedCustomer}
            onSelectedCustomerChange={handleSelectedCustomerChange}
            address={address}
            phone={phone}
            email={email}
            invoiceTo={invoiceTo}
            vat={vat}
            invoiceAddress={invoiceAddress}
            invoiceEmail={invoiceEmail}
            onAddressChange={handleAddressChange}
            onPhoneChange={setPhone}
            onEmailChange={handleEmailChange}
            onInvoiceToChange={handleInvoiceToChange}
            onVatChange={setVat}
            onInvoiceAddressChange={handleInvoiceAddressChange}
            onInvoiceEmailChange={handleInvoiceEmailChange}
            fieldNames={fieldNames}
            isEdit={isEdit}
            bookingExtra={bookingExtra}
            renderDayBookings={props?.renderDayBookings}
          />
          {errorNotice}
          {actions}
        </form>
      </div>
    )
  }

  return (
    <div className="bg-slate-50 text-slate-900">
      <main className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-6">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold">
            {isEdit ? 'Edit Callout' : 'New Callout'}
          </h1>
          <p className="text-sm text-slate-600">
            {isEdit
              ? 'Update the day/time and details for this Callout booking.'
              : 'Find a day/time and capture the details needed to book a new Callout.'}
          </p>
        </header>

        <form
          className="flex flex-col gap-6"
          autoComplete="new-password"
          onSubmit={handleSubmit}
        >
          <div className="rounded-md bg-background p-3 sm:p-6 text-ink shadow-sm">
            <NewCalloutFormSections
              initial={initial}
              day={day}
              onDayChange={setDay}
              time={time}
              endTime={endTime}
              onTimeChange={handleTimeChange}
              onEndTimeChange={handleEndTimeChange}
              selectedCustomer={selectedCustomer}
              onSelectedCustomerChange={handleSelectedCustomerChange}
              address={address}
              phone={phone}
              email={email}
              invoiceTo={invoiceTo}
              vat={vat}
              invoiceAddress={invoiceAddress}
              invoiceEmail={invoiceEmail}
              onAddressChange={handleAddressChange}
              onPhoneChange={setPhone}
              onEmailChange={handleEmailChange}
              onInvoiceToChange={handleInvoiceToChange}
              onVatChange={setVat}
              onInvoiceAddressChange={handleInvoiceAddressChange}
              onInvoiceEmailChange={handleInvoiceEmailChange}
              fieldNames={fieldNames}
              isEdit={isEdit}
              bookingExtra={bookingExtra}
              renderDayBookings={props?.renderDayBookings}
            />
          </div>

          {errorNotice}
          {actions}
        </form>
      </main>
    </div>
  )
}
